"use client";

import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {isPresentationRecord, presentedFields, presentationText, type PresentedField} from "@repo/ui/gailgpt/tool-presentation";
import {usePhoneSurveysFeatureEnabled} from "@/hooks/usePhoneSurveysFeatureEnabled";
import {CollectionReview, ReviewFields, type ToolDetailsProps} from "@/components/chat/ToolPresentationDetails";
import {useVerifyScriptVoice} from "@/hooks/useVerifyScriptVoice";
import {listOrgOutcomes, type OrgOutcomeDto} from "@/services/orgOutcomes";
import {occurrenceKeys, valueKey} from "@/components/chat/snapshotKeys";
import {formatOutcomeKeyLabel} from "@/utils/orgOutcomes/formatOutcomeKeyLabel";
import {useApprovalAssociationNames} from "@/components/chat/ApprovalReferenceValue";

function ApplicableOutcomes({field}: {field: PresentedField & {value: string[]}}) {
   const {t} = useTranslation("ask");
   const [catalog, setCatalog] = useState<OrgOutcomeDto[]>([]);
   useEffect(() => {
      let active = true;
      void listOrgOutcomes().then(result => {
         if (active && result.success && result.data) setCatalog(result.data);
      }).catch(() => {});
      return () => { active = false; };
   }, []);
   const definitions = new Map(catalog.map(outcome => [outcome.key, outcome.definition]));
   return <section className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-4" aria-label={presentationText(field.label, t)}>
      <h4 className="text-sm font-normal text-500 [overflow-wrap:anywhere]">{presentationText(field.label, t)}</h4>
      <ul className="space-y-2">
         {occurrenceKeys(field.value, valueKey).map(({item: key, key: occurrenceKey}) => <li key={occurrenceKey}>
            <p className="text-sm text-700 [overflow-wrap:anywhere]">{formatOutcomeKeyLabel(key)}</p>
            {definitions.get(key) && <p className="mt-1 text-sm leading-relaxed text-600 [overflow-wrap:anywhere]">{definitions.get(key)}</p>}
         </li>)}
      </ul>
   </section>;
}

function ScriptVoice({field}: {field: PresentedField & {value: string}}) {
   const {t} = useTranslation("common");
   const {voice} = useVerifyScriptVoice(field.entityName ? undefined : field.value);
   const name = field.entityName || (voice?.id === field.value ? voice.name : undefined);
   return <ReviewFields compact fields={[{...field, format: "entity", entityName: name || t("unknown")}]} />;
}

function suppliedAssociationName(record: Record<string, unknown>) {
   return [record.title, record.name].find((value): value is string => typeof value === "string" && !!value.trim());
}

function UseCaseAssociations({field}: {field: PresentedField & {value: unknown[]}}) {
   const {t} = useTranslation("ask");
   const records = field.value.every(isPresentationRecord) ? field.value as Record<string, unknown>[] : null;
   const names = useApprovalAssociationNames((records ?? []).map((record, index) => ({
      id: typeof record.id === "string" ? record.id : "", name: suppliedAssociationName(record),
      path: {source: "request", segments: [...field.path, String(index), "id"]},
   })));
   const associationName = (index: number) => names[index] || t("toolApproval.execution.unavailableUseCase");
   return <CollectionReview field={field} initiallyOpen={false}
      preview={records ? <>{records.slice(0, 2).map((_, index) => associationName(index)).join(" · ")}{records.length > 2 ? t("toolApproval.execution.moreItems", {count: records.length - 2}) : ""}</> : undefined}
      columnLabel={column => column === "id" ? t("toolApproval.execution.useCase") : undefined}
      renderCell={(_record, column, index) => column === "id" ? associationName(index) : undefined} />;
}

function renderScriptField(field: PresentedField) {
   if (field.path.at(-1) === "toolAssociations" && Array.isArray(field.value) && field.value.every((id): id is string => typeof id === "string"))
      return <CollectionReview field={{...field, value: field.value}} />;
   if (field.path.at(-1) === "applicableOutcomes" && Array.isArray(field.value) && field.value.length && field.value.every((key): key is string => typeof key === "string"))
      return <ApplicableOutcomes field={{...field, value: field.value}} />;
   if (field.path.at(-1) === "voice" && typeof field.value === "string" && field.value)
      return <ScriptVoice key={field.value} field={{...field, value: field.value}} />;
   if (field.path.at(-1) === "useCaseAssociations" && Array.isArray(field.value) && field.value.length)
      return <UseCaseAssociations field={{...field, value: field.value}} />;
   if (field.path.at(-1) === "documentIds" && Array.isArray(field.value) && field.value.length && field.value.every((id): id is string => typeof id === "string"))
      return <CollectionReview field={{...field, value: field.value}} />;
   if (field.path.at(-1) === "faqs" && Array.isArray(field.value) && field.value.length)
      return <CollectionReview field={{...field, value: field.value.map(item => isPresentationRecord(item)
         ? Object.fromEntries(Object.entries(item).filter(([key]) => key !== "details")) : item)}} />;
}

const unconfigured = ({value}: PresentedField) => value == null || value === "" ||
   (Array.isArray(value) && !value.length) || (isPresentationRecord(value) && !Object.keys(value).length);

export function ScriptDetails({fields}: {fields: PresentedField[]}) {
   const {t} = useTranslation("ask");
   const phoneSurveysEnabled = usePhoneSurveysFeatureEnabled();
   // The provider is chosen from the voice, so the review shows the voice alone.
   const visibleFields = fields.filter(field => !["voiceProvider", "body.voiceProvider"].includes(field.path.join(".")))
      .filter(field => phoneSurveysEnabled ||
      !(field.path.join(".") === "giveSurvey" || field.path.join(".") === "body.giveSurvey"))
      .map(field => field.path.at(-1) === "direction" ? {...field, choices: [
         {value: "inbound", label: {text: t("workspace.scriptDraft.directionInbound")}},
         {value: "outbound", label: {text: t("workspace.scriptDraft.directionOutbound")}},
      ]} : field);
   const configured = visibleFields.filter(field => !unconfigured(field));
   const isContent = (field: PresentedField) => field.emphasis === "primary" || field.format === "multiline" ||
      ["name", "agentName", "direction", "greeting", "goodbye", "useCaseAssociations"].includes(field.path.at(-1) ?? "");
   const content = configured.filter(isContent);
   const settings = configured.filter(field => !isContent(field));
   return <div className="space-y-5">
      {!!content.length && <ReviewFields fields={content} renderField={renderScriptField} />}
      {!!settings.length && <section className="border-t border-neutral pt-4" aria-label={t("toolApproval.createScript.settings")}>
         <h4 className="mb-3 text-xs font-medium text-500">{t("toolApproval.createScript.settings")}</h4>
         <ReviewFields fields={settings} compact renderField={renderScriptField} />
      </section>}
   </div>;
}

/** Adapts a tool request to the same script details used by settled reviews. */
export default function ScriptToolDetails({presentation, parameters}: ToolDetailsProps) {
   const {t} = useTranslation("ask");
   const fields = presentedFields(parameters, presentation.fields);
   return <div className="px-5 pb-5 pt-4">
      <ScriptDetails fields={fields} />
      {!fields.length && <p className="text-sm text-500">{t("toolApproval.execution.noParameters")}</p>}
   </div>;
}
